import { systemApiClient } from '@/shared/api/core/axios-instance';
import { PROMETHEUS_CONFIG } from '@/shared/config/prometheus-config';

import { calculateStats, createTimeRangeParams, extractGpuMetricBase } from './gpuUtils';
import type {
  GpuMemoryUsedMetric,
  GpuPowerMetric,
  GpuTempMetric,
  MetricDataPoint,
  PodMetric,
  PrometheusMatrixResult,
  PrometheusVectorResult,
  TensorCoreMetric,
  VramUtilizationMetric,
} from './types';

const PROMETHEUS_NAMESPACE = PROMETHEUS_CONFIG.NAMESPACE;

const parseValue = (value: string): number => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const extractPodDisplayName = (podName: string): string => {
  const parts = podName.split('-');
  if (parts.length > 2) {
    return parts.slice(0, -2).join('-').replace(`${PROMETHEUS_NAMESPACE}-`, '');
  }
  return podName.replace(`${PROMETHEUS_NAMESPACE}-`, '');
};

const createGpuMemoryDisplayName = (exportedNamespace: string, exportedPod: string) => {
  if (!exportedNamespace && !exportedPod) {
    return 'unknown namespace/pod';
  }

  const workloadSegment =
    exportedNamespace && exportedPod ? `${exportedNamespace}/${exportedPod}` : exportedNamespace || exportedPod;

  return workloadSegment;
};

const createGpuMemoryUsedMetric = (
  metric: PrometheusMatrixResult['metric'] | PrometheusVectorResult['metric'],
  data: MetricDataPoint[]
): GpuMemoryUsedMetric => {
  const base = extractGpuMetricBase(metric);
  const exportedNamespace = metric.exported_namespace || '';
  const exportedPod = metric.exported_pod || '';
  const exportedContainer = metric.exported_container || '';
  const gpuInstanceProfile = metric.GPU_I_PROFILE || '';
  const modelName = metric.modelName || '';
  const uuid = metric.UUID || '';
  const node = metric.node || base.hostname;
  const displayName = createGpuMemoryDisplayName(exportedNamespace, exportedPod);
  const values = data.map((point) => point.value);
  const stats = calculateStats(values);

  return {
    ...base,
    displayName,
    gpuInstanceProfile,
    modelName,
    exportedNamespace,
    exportedPod,
    exportedContainer,
    uuid,
    node,
    data,
    ...stats,
  };
};

export const queryInstant = async (query: string): Promise<PrometheusVectorResult[]> => {
  // 백엔드 프록시는 result 배열을 직접 반환
  const response = await systemApiClient.get<PrometheusVectorResult[]>('/monitoring/metrics/instant', {
    params: {
      query,
    },
  });

  return response.data;
};

export const queryRange = async (
  query: string,
  start: number,
  end: number,
  step: number
): Promise<PrometheusMatrixResult[]> => {
  // 백엔드 프록시는 result 배열을 직접 반환
  const response = await systemApiClient.get<PrometheusMatrixResult[]>('/monitoring/metrics/range', {
    params: {
      query,
      start: start.toString(),
      end: end.toString(),
      step: step.toString(),
    },
  });

  return response.data;
};

export const getInstantValue = async (query: string): Promise<number> => {
  const result = await queryInstant(query);
  if (result.length === 0) return 0;
  return parseValue(result[0].value[1]);
};

export const getInstantValueOrNull = async (query: string): Promise<number | null> => {
  const result = await queryInstant(query);
  if (result.length === 0) return null;
  return parseValue(result[0].value[1]);
};

export const getValuesByPod = async (query: string): Promise<PodMetric[]> => {
  const result = await queryInstant(query);
  return result.map((item) => ({
    pod: item.metric.pod || 'unknown',
    displayName: extractPodDisplayName(item.metric.pod || 'unknown'),
    value: parseValue(item.value[1]),
  }));
};

export const getRangeValues = async (
  query: string,
  rangeSeconds: number = 3600,
  stepSeconds: number = 60,
  nowTimestamp?: number
): Promise<MetricDataPoint[]> => {
  const now = nowTimestamp || Math.floor(Date.now() / 1000);
  const start = now - rangeSeconds;

  const result = await queryRange(query, start, now, stepSeconds);

  if (result.length === 0) return [];

  return result[0].values.map(([timestamp, value]) => ({
    timestamp: timestamp * 1000,
    value: parseValue(value),
  }));
};

export const getRangeValuesByPod = async (
  query: string,
  rangeSeconds: number = 3600,
  stepSeconds: number = 60,
  nowTimestamp?: number
): Promise<{ pod: string; data: MetricDataPoint[] }[]> => {
  const now = nowTimestamp || Math.floor(Date.now() / 1000);
  const start = now - rangeSeconds;

  const result = await queryRange(query, start, now, stepSeconds);

  return result.map((item) => ({
    pod: item.metric.pod || 'unknown',
    data: item.values.map(([timestamp, value]) => ({
      timestamp: timestamp * 1000,
      value: parseValue(value),
    })),
  }));
};

export const getAvailableWorkers = async (): Promise<string[]> => {
  const response = await systemApiClient.get<string[]>('/monitoring/metrics/labels/Hostname/values');
  return response.data.sort();
};

/**
 * GPU 온도 데이터 조회
 * @param hostname - Worker hostname
 * @returns GPU 온도 메트릭 배열
 */
export const getGpuTemperatures = async (hostname: string): Promise<GpuTempMetric[]> => {
  const query = `DCGM_FI_DEV_GPU_TEMP{Hostname="${hostname}"}`;
  const { start, end, step } = createTimeRangeParams();
  const result = await queryRange(query, start, end, step);

  return result.map((item) => {
    const base = extractGpuMetricBase(item.metric);

    // 빈 배열 체크: NaN 방지
    const totalTemperature = item.values.reduce((sum, [_timestamp, value]) => {
      return sum + parseValue(value);
    }, 0);
    const avgTemperature = item.values.length > 0 ? totalTemperature / item.values.length : 0;

    return {
      ...base,
      temperature: avgTemperature,
    };
  });
};

/**
 * Tensor Core 활용률 조회
 * @param hostname - Worker hostname
 * @returns Tensor Core 메트릭 배열 (시계열 데이터 포함)
 */
export const getTensorCoreUtilization = async (hostname: string): Promise<TensorCoreMetric[]> => {
  const query = `DCGM_FI_PROF_PIPE_TENSOR_ACTIVE{Hostname="${hostname}"}`;
  const { start, end, step } = createTimeRangeParams();
  const result = await queryRange(query, start, end, step);

  return result.map((item) => {
    const base = extractGpuMetricBase(item.metric);

    const data = item.values.map(([timestamp, value]) => ({
      timestamp: timestamp * 1000,
      value: parseValue(value) * 100,
    }));

    const values = data.map((d) => d.value);
    const stats = calculateStats(values);

    return {
      ...base,
      data,
      ...stats,
    };
  });
};

/**
 * GPU 전력 사용량 조회
 * @param hostname - Worker hostname
 * @returns GPU 전력 메트릭 배열
 */
export const getGpuPowerUsage = async (hostname: string): Promise<GpuPowerMetric[]> => {
  const query = `DCGM_FI_DEV_POWER_USAGE{Hostname="${hostname}"} / 100`;
  const { start, end, step } = createTimeRangeParams();
  const result = await queryRange(query, start, end, step);

  return result.map((item) => {
    const base = extractGpuMetricBase(item.metric);

    const totalPowerUsage = item.values.reduce((sum, [_timestamp, value]) => {
      return sum + parseValue(value);
    }, 0);

    return {
      ...base,
      powerUsage: totalPowerUsage,
    };
  });
};

/**
 * GPU 메모리 사용량 조회
 * @returns GPU 메모리 사용량 메트릭 배열 (MiB 시계열 데이터 포함)
 */
export const getGpuMemoryUsed = async (): Promise<GpuMemoryUsedMetric[]> => {
  const query = 'DCGM_FI_DEV_FB_USED';
  const { start, end, step } = createTimeRangeParams();
  const result = await queryRange(query, start, end, step);

  return result.map((item) => {
    const data = item.values.map(([timestamp, value]) => ({
      timestamp: timestamp * 1000,
      value: parseValue(value),
    }));

    return createGpuMemoryUsedMetric(item.metric, data);
  });
};

/**
 * GPU 메모리 사용량 요약 조회
 * @returns GPU 메모리 사용량 메트릭 배열 (현재 MiB 값)
 */
export const getGpuMemoryUsedSummary = async (): Promise<GpuMemoryUsedMetric[]> => {
  const query = 'DCGM_FI_DEV_FB_USED';
  const result = await queryInstant(query);

  return result.map((item) => {
    const data = [
      {
        timestamp: item.value[0] * 1000,
        value: parseValue(item.value[1]),
      },
    ];

    return createGpuMemoryUsedMetric(item.metric, data);
  });
};

/**
 * GPU VRAM 사용률 조회
 * @param hostname - Worker hostname
 * @returns VRAM 사용률 메트릭 배열 (시계열 데이터 포함)
 */
export const getVramUtilization = async (hostname: string): Promise<VramUtilizationMetric[]> => {
  const query = `DCGM_FI_DEV_FB_USED{Hostname="${hostname}"} / DCGM_FI_DEV_FB_FREE{Hostname="${hostname}"} * 100`;
  const { start, end, step } = createTimeRangeParams();
  const result = await queryRange(query, start, end, step);

  return result.map((item) => {
    const base = extractGpuMetricBase(item.metric);

    const data = item.values.map(([timestamp, value]) => ({
      timestamp: timestamp * 1000,
      value: parseValue(value),
    }));

    const values = data.map((d) => d.value);
    const stats = calculateStats(values);

    return {
      ...base,
      data,
      ...stats,
    };
  });
};
